import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  BookOpen, Globe, Zap,
  ArrowRight, ExternalLink, Lock, Cpu
} from 'lucide-react';
import './eLearning.css';

const PLATFORM_URL = 'https://academy.quantumke.org/';


const whyItems = [
  {
    icon: Lock,
    title: 'Removing the Access Barrier',
    body: 'Quantum education has historically been concentrated in a handful of elite institutions. Quantumke Academy tears that wall down by putting world-class curriculum in the hands of anyone across Africa and beyond.',
  },
  {
    icon: Cpu,
    title: 'Purpose-Built for Quantum',
    body: 'Unlike generic eLearning platforms, Quantumke Academy is designed specifically for quantum science and technology programs — the content, structure, and community are quantum-native.',
  },
  {
    icon: BookOpen,
    title: 'Aligned with Global Standards',
    body: 'Programs are developed in collaboration with QWorld, Strathmore University, and global quantum partners, ensuring learners meet internationally recognised learning benchmarks.',
  },
  {
    icon: Zap,
    title: 'Bridging the Quantum Gap',
    body: "Africa's quantum talent gap is real. Quantumke Academy is QuantumKE's direct response — creating a scalable pipeline of quantum-literate individuals ready to contribute to research, industry, and policy.",
  },
];

const ELearningPage = () => (
  <div className="elearn-page">
    <Helmet>
      <title>eLearning | Quantumke Academy | QuantumKE</title>
      <meta name="description" content="Access quantum education through Quantumke Academy — Africa's quantum academy network. Purpose-built quantum science courses for learners across Kenya and the continent." />
      <link rel="canonical" href="https://quantumke.org/academy" />
    </Helmet>

    {/* ── HERO ── */}
    <section className="elearn-hero">
      <div className="elearn-hero-grid-bg" aria-hidden />
      <div className="elearn-hero-glow elearn-hero-glow-1" aria-hidden />
      <div className="elearn-hero-glow elearn-hero-glow-2" aria-hidden />

      <div className="elearn-hero-content">
        <h1 className="elearn-hero-title">
          Everything You Need to <br />
          <span className="elearn-hero-gradient">Start in Quantum</span>
        </h1>

        <p className="elearn-hero-subtitle">
          Quantumke Academy — the Africa Quantum Education Network — is QuantumKE's dedicated open eLearning
          platform for quantum science &amp; technology programs. Structured, accessible, and
          built for Africa.
        </p>

        <div className="elearn-hero-actions">
          <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" className="elearn-btn-primary">
            Launch Platform
            <ExternalLink size={16} />
          </a>
          <a href="#why" className="elearn-btn-ghost">
            Learn More
            <ArrowRight size={16} />
          </a>
        </div>

        <div className="elearn-hero-stats">
          <div className="elearn-hero-stat">
            <span className="elearn-hero-stat-value">Open</span>
            <span className="elearn-hero-stat-label">Free Access</span>
          </div>
          <div className="elearn-hero-stat-divider" />
          <div className="elearn-hero-stat">
            <span className="elearn-hero-stat-value">Quantum</span>
            <span className="elearn-hero-stat-label">Focused Programs</span>
          </div>
          <div className="elearn-hero-stat-divider" />
          <div className="elearn-hero-stat">
            <span className="elearn-hero-stat-value">Africa</span>
            <span className="elearn-hero-stat-label">First Design</span>
          </div>
        </div>
      </div>

    </section>

    {/* ── WHY IT EXISTS ── */}
    <section className="elearn-why-section" id="why">
      <div className="elearn-container">
        <div className="elearn-section-header">
          <span className="elearn-label">Why Quantumke Academy Exists</span>
          <h2 className="elearn-section-title">
            Democratising Quantum Knowledge
          </h2>
          <p className="elearn-section-desc">
            The world is entering the quantum era — and Africa cannot be left behind. Quantumke Academy was
            built to close the gap between global quantum progress and local access to quality
            quantum education.
          </p>
        </div>

        <div className="elearn-why-grid">
          {whyItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="elearn-why-card">
                <div className="elearn-why-icon-wrap">
                  <Icon size={22} />
                </div>
                <h3 className="elearn-why-title">{item.title}</h3>
                <p className="elearn-why-body">{item.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ── OPEN PLATFORM CALLOUT ── */}
    <section className="elearn-open-section">
      <div className="elearn-container">
        <div className="elearn-open-card">
          <div className="elearn-open-glow" aria-hidden />
          <div className="elearn-open-content">
            <span className="elearn-open-badge">Open Platform</span>
            <h2 className="elearn-open-title">No Cost. No Barriers. Just Learning.</h2>
            <p className="elearn-open-desc">
              Quantumke Academy is and always will be free. Whether you are a university student in Nairobi,
              a self-taught programmer in Lagos, or a researcher in Accra — the platform is yours.
              All you need is curiosity and a connection.
            </p>
            <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" className="elearn-btn-primary">
              Start Learning Free
              <ExternalLink size={16} />
            </a>
          </div>
          <div className="elearn-open-illustration" aria-hidden>
            <div className="elearn-open-circle elearn-open-circle-lg" />
            <div className="elearn-open-circle elearn-open-circle-md" />
            <div className="elearn-open-circle elearn-open-circle-sm" />
            <Globe size={56} className="elearn-open-icon" />
          </div>
        </div>
      </div>
    </section>

  </div>
);

export default ELearningPage;
